/**
 * Lectura DEFENSIVA del detalle por celda que manda el backend cuando rechaza un CSV
 * (`status: 'METRIC_DATA_NOT_VALID'`).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * REGLA DE LA CASA (nació de un bug real, 2026-09-01):
 *
 *   Ningún catálogo indexado por una clave que viene del BACKEND se accede directo
 *   (`CATALOGO[clave]['campo']`). Siempre por un resolver con FALLBACK TOTAL.
 *
 * El modal de "Agregar cuenta" del canal SHEET hacía exactamente eso: leía
 * `PROPERTIES_FOR_METRIC[traducir(data.error_message)]["Spanish"]` dentro de un `.then()` sin
 * `.catch()`. Con `error_message: "DATE"` el traductor devolvía "DATE" tal cual, el catálogo daba
 * `undefined` y el `["Spanish"]` tiraba un TypeError que, sin `.catch()`, quedaba como una promise
 * rejection sin manejar: el usuario apretaba el botón y NO PASABA ABSOLUTAMENTE NADA.
 *
 * Una clave nueva del backend (`DATE`, `ADGROUP_NAME`, la que inventen mañana) tiene que DEGRADAR
 * a texto legible, nunca romper.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 *
 * Además: el backend y el frontend NO deployan juntos. Todo lo de este archivo es *progressive
 * enhancement* sobre `error_message`, que sigue llegando como siempre. Si `errors` no viene, no
 * parsea, o trae otra forma, devolvemos `null` y el llamador cae al mensaje genérico.
 */

import {DATA_BY_COLUMN} from "./columns";

// `reason` es un enum CERRADO del backend. Todo lo que no esté acá degrada al mensaje genérico:
// un reason nuevo tiene que verse feo, no romper la pantalla.
export const REASON_DATE_FORMAT_MISMATCH = 'DATE_FORMAT_MISMATCH';
export const REASON_EMPTY_VALUE = 'EMPTY_VALUE';
export const REASON_NOT_A_NUMBER = 'NOT_A_NUMBER';
export const REASON_NOT_AN_INTEGER = 'NOT_AN_INTEGER';
// La fecha SE PUDO leer pero no existe en el calendario (`2026-02-31`). El backend la corre sola
// al último día del mes por un fallback de Looker Studio, así que sin reportarlo la planilla se
// aprueba "sin problemas" y la cuenta queda con el dato corrido. Ojo: el formato de la columna
// está BIEN — por eso tiene razón propia y no se le ofrece cambiar el formato.
export const REASON_DATE_DOES_NOT_EXIST = 'DATE_DOES_NOT_EXIST';
export const REASON_UNKNOWN = 'UNKNOWN';

export const KNOWN_REASONS = [
    REASON_DATE_FORMAT_MISMATCH,
    REASON_EMPTY_VALUE,
    REASON_NOT_A_NUMBER,
    REASON_NOT_AN_INTEGER,
    REASON_DATE_DOES_NOT_EXIST,
    REASON_UNKNOWN
];

// Bucket para issues que llegan sin `column` (o con una columna que no mapeamos): se muestran en
// el resumen general en vez de perderse.
export const UNKNOWN_COLUMN = '';

// Cuántos issues se listan inline en la fila del mapeo antes de resumir con "y N más".
export const MAX_INLINE_ISSUES = 3;

// Cuántos números de fila se enumeran en el resumen antes de cortar. Listar 50 no ayuda a nadie.
export const MAX_SUMMARY_ROWS = 6;

/**
 * Nombre legible de una columna del CSV. Fallback total, en este orden:
 *   1. la key i18n `sheet_account.column.<CLAVE>` (traducible),
 *   2. el catálogo legacy `DATA_BY_COLUMN`,
 *   3. la clave cruda del backend.
 * Nunca devuelve `undefined` ni lanza.
 */
export function resolveColumnLabel(column, t) {
    if (column === null || column === undefined || column === '') return '';
    const key = String(column);
    const legacy = DATA_BY_COLUMN[key] && DATA_BY_COLUMN[key].Spanish;
    const fallback = legacy || key;
    if (typeof t !== 'function') return fallback;
    return t('sheet_account.column.' + key, {
        defaultValue: fallback,
        interpolation: {escapeValue: false}
    });
}

function toFiniteNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * `errors` viene como STRING con JSON adentro (el backend hace `json.dumps`). Puede también venir
 * ya parseado, como `[]` (el default que pone APIClient cuando el body no lo trae), o no venir.
 * Devuelve `null` ante cualquier duda — el llamador cae al comportamiento de siempre.
 */
export function parseSheetIssues(errors) {
    if (!errors) return null;

    let payload = errors;
    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch (e) {
            return null;
        }
    }

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;

    const detail = payload.sheet_issues;
    if (!detail || typeof detail !== 'object' || !Array.isArray(detail.issues)) return null;

    const issues = detail.issues.filter((issue) => issue && typeof issue === 'object');

    return {
        issues: issues,
        rowsScanned: toFiniteNumber(detail.rows_scanned),
        rowsOk: toFiniteNumber(detail.rows_ok),
        // Un límite silencioso es lo que nos trajo hasta acá: si el backend lo dice, lo decimos.
        truncated: detail.truncated === true,
        issuesCapped: detail.issues_capped === true,
        // `truncated` y `exceedsRowLimit` NO son lo mismo y el backend los manda por separado a
        // propósito (`SheetValidationReport.mark_exceeds_row_limit`): `truncated` = "miramos las
        // primeras N filas, la cuenta se creó igual"; `exceedsRowLimit` = "el archivo tiene más
        // filas de las que el sync puede leer, la cuenta NO se creó". Hasta acá se leía sólo el
        // primero, así que el rechazo por tamaño llegaba sin sus números y el modal caía al mensaje
        // genérico de "intentar de nuevo en unos minutos" — una instrucción falsa: reintentar el
        // mismo archivo da exactamente el mismo resultado.
        exceedsRowLimit: detail.exceeds_row_limit === true,
        rowsInFile: toFiniteNumber(detail.rows_in_file),
        maxRowsSupported: toFiniteNumber(detail.max_rows_supported)
    };
}

/** `{DATE: [issue, ...], ...}` respetando el orden de llegada. */
export function groupIssuesByColumn(issues) {
    const grouped = {};
    (issues || []).forEach((issue) => {
        const column = (issue && issue.column !== null && issue.column !== undefined)
            ? String(issue.column)
            : UNKNOWN_COLUMN;
        if (!grouped[column]) grouped[column] = [];
        grouped[column].push(issue);
    });
    return grouped;
}

function issueValue(issue) {
    if (!issue || issue.value === null || issue.value === undefined) return '';
    return String(issue.value);
}

/**
 * Texto de UN issue: "Fila 7: falta el valor de «Fecha»" / "Fila 7: «2026-08-31» no coincide con
 * el formato DD/MM/YYYY".
 *
 * ⚠️ i18next escapa los valores interpolados a entidades HTML: sin `escapeValue: false` un formato
 * de fecha sale como `DD&#x2F;MM&#x2F;YYYY`.
 */
export function describeIssue(issue, {t, dateFormat} = {}) {
    if (typeof t !== 'function') return '';

    const reason = (issue && KNOWN_REASONS.includes(issue.reason)) ? issue.reason : 'generic';
    const params = {
        row: (issue && issue.row !== null && issue.row !== undefined) ? issue.row : '?',
        value: issueValue(issue),
        column: resolveColumnLabel(issue && issue.column, t),
        format: dateFormat || '',
        interpolation: {escapeValue: false}
    };

    // Sin formato de fecha configurado no podemos decir "no coincide con X".
    if (reason === REASON_DATE_FORMAT_MISMATCH && !dateFormat) {
        return t('sheet_account.issues.reason.DATE_FORMAT_MISMATCH_no_format', params);
    }
    return t('sheet_account.issues.reason.' + reason, params);
}

/**
 * La mejor parte de la feature: si el backend sugiere un arreglo concreto lo convertimos en un
 * click. Hoy sólo entendemos `{"date_format": "YYYY-MM-DD"}`; cualquier otra forma devuelve null
 * (no se ofrece botón, el issue se muestra igual).
 */
export function suggestionForIssue(issue) {
    const suggestion = issue && issue.suggestion;
    if (!suggestion || typeof suggestion !== 'object') return null;
    if (typeof suggestion.date_format === 'string' && suggestion.date_format) {
        return {type: 'DATE_FORMAT', value: suggestion.date_format};
    }
    return null;
}

/** Números de fila para el resumen: "7, 45, 102" (cortado en MAX_SUMMARY_ROWS). */
export function summarizeRows(issues) {
    const rows = [];
    (issues || []).forEach((issue) => {
        if (!issue || issue.row === null || issue.row === undefined) return;
        if (rows.indexOf(issue.row) === -1) rows.push(issue.row);
    });
    const shown = rows.slice(0, MAX_SUMMARY_ROWS).join(', ');
    return {count: rows.length, text: rows.length > MAX_SUMMARY_ROWS ? shown + '…' : shown};
}
